import { takeLatest, put } from "redux-saga/effects";
import { SEARCH_DOCTORS } from "./constants";
import { GWatcher } from "types/redux";
import { SearchDoctorsPayload } from 'types/payload';
import { FamilyDoctor } from 'types/entity';
import { request } from "lib/request"
import actions from "./actions";

const SEARCH_LIMIT = 48
const SEARCH_ROUTE = '/annuaire/customers/doctors/search'

const buildFullName = (doctor: FamilyDoctor) => `${doctor.firstname} ${doctor.lastname}`.toLowerCase()

// Strip accents first since JS string comparison isn't accent-folding like the DB collation.
const stripAccents = (value: string) => value.normalize("NFD").replace(/\p{Diacritic}/gu, "")
const tokenize = (value: string) => stripAccents(value.toLowerCase().trim()).split(/[\s\-']+/).filter(Boolean)

// The API only casts a broad net (any typed word prefixing firstname or lastname), since a
// typed phrase can't be reliably split into firstname/lastname (compound names, particles,
// unknown order). This does the precise fit: every typed word must prefix some word in the
// doctor's full name, in any order, tolerating extra words in between (e.g. a middle name).
const matchesFullName = (doctor: FamilyDoctor, typedName: string) => {
  const nameTokens = tokenize(buildFullName(doctor))
  const typedTokens = tokenize(typedName)
  return typedTokens.every((token) => nameTokens.some((nameToken) => nameToken.startsWith(token)))
}

function* searchDoctors({payload}: {type: string, payload: SearchDoctorsPayload}) {
  try {
    const page = payload?.page || 1;
    const res = yield request(`${SEARCH_ROUTE}?limit=${SEARCH_LIMIT}&page=${page}`, {
      method: 'POST',
      payload: { fullname: payload?.fullname, rpps: payload?.rpps, profession: payload?.profession },
    })
    const rawDoctors: FamilyDoctor[] = res.data?.doctors || []
    const doctors = payload?.fullname ? rawDoctors.filter((doctor) => matchesFullName(doctor, payload.fullname as string)) : rawDoctors
    // Fall back to the full-page heuristic if the API response omits `total`.
    const total = res.data?.total ?? (rawDoctors.length === SEARCH_LIMIT ? rawDoctors.length + 1 : rawDoctors.length)

    if (page > 1) {
      yield put(actions.appendDoctorList({ doctors, total }));
    } else {
      yield put(actions.setSearchedDoctorList({ doctors, total }));
    }
  } catch (error) {
    console.error(error, {
      route: SEARCH_ROUTE
    });
    // Clear loading on unexpected errors so the screen doesn't stay stuck spinning.
    yield put(actions.setSearchedDoctorList({ doctors: [], total: 0 }));
  }
}

function * doctorsWatcher() : GWatcher {
  yield takeLatest(SEARCH_DOCTORS, searchDoctors)
}

export default doctorsWatcher;
